import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactToPrint from "react-to-print";
import { Button } from "react-bootstrap";
import { FileEarmarkText } from "react-bootstrap-icons";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import MicIcon from "@material-ui/icons/Mic";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import roboResponde from "./img/robo-conecta-responde.svg";
import ChatMessage from "./chat-message";
import RepondeurPrint from "./repondeur-print";
import {
	saudacaoPorHora,
	getNomeUsuario,
	extrairCoresDaMarca,
	isCoarsePointer,
} from "./repondeur-utils";
import logoResponde from "./img/logo-conecta-responde.svg";

// fração da distância vencida a cada 16,7ms: equilíbrio entre responder
// rápido ao texto novo e manter o movimento contínuo, sem salto
const SUAVIDADE = 0.16;

const prefereMenosMovimento = () =>
	typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const CORES_PADRAO = ["#00c0b6", "#b3d432", "#f2d200"];

const SpeechRecognitionImpl =
	typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

const ChatWindow = ({ chat }) => {
	const [draft, setDraft] = useState("");
	const [coresMarca, setCoresMarca] = useState(CORES_PADRAO);
	const [gerandoPdf, setGerandoPdf] = useState(false);
	const inicioPdfRef = useRef(0);
	const printRef = useRef(null);
	const [stick, setStick] = useState(true);
	const [recording, setRecording] = useState(false);
	const [micLabel, setMicLabel] = useState("");
	const [micError, setMicError] = useState("");
	const scrollRef = useRef(null);
	const rodapeRef = useRef(null);
	const textareaRef = useRef(null);
	const recognitionRef = useRef(null);
	const draftBaseRef = useRef("");

	const rolagemAutomaticaRef = useRef(false);
	const quadroRef = useRef(null);
	const instanteRef = useRef(0);

	// deslize único do chat: persegue o fim com easing recalculando o alvo vivo
	// a cada quadro, então mensagem nova e texto crescendo são absorvidos pelo
	// mesmo movimento contínuo; ritmo compensado pelo tempo (60Hz e 120Hz iguais)
	const irParaOFim = () => {
		const el = scrollRef.current;
		if (!el) return;
		if (prefereMenosMovimento()) {
			el.scrollTop = el.scrollHeight;
			return;
		}
		if (quadroRef.current) return;

		const passo = instante => {
			const atual = scrollRef.current;
			if (!atual) {
				quadroRef.current = null;
				return;
			}
			const distancia = atual.scrollHeight - atual.clientHeight - atual.scrollTop;
			if (distancia < 0.5) {
				atual.scrollTop = atual.scrollHeight - atual.clientHeight;
				quadroRef.current = null;
				rolagemAutomaticaRef.current = false;
				return;
			}
			const decorrido = instanteRef.current ? Math.min(instante - instanteRef.current, 50) : 16.7;
			instanteRef.current = instante;
			rolagemAutomaticaRef.current = true;
			atual.scrollTop += distancia * (1 - Math.pow(1 - SUAVIDADE, decorrido / 16.7));
			quadroRef.current = window.requestAnimationFrame(passo);
		};

		instanteRef.current = 0;
		quadroRef.current = window.requestAnimationFrame(passo);
	};

	const caudaRef = useRef(null);

	// acompanha a conversa: com rolagem disponível, o deslize suave persegue o
	// fim; sem rolagem (conversa curta), o empurrão que o texto dá no
	// pensamento é suavizado com o mesmo easing, via transform
	useLayoutEffect(() => {
		const el = scrollRef.current;
		if (!el || !stick) return;

		if (el.scrollHeight > el.clientHeight) {
			irParaOFim();
			return;
		}

		const cauda = caudaRef.current;
		if (!cauda || prefereMenosMovimento()) return;
		const visual = cauda.getBoundingClientRect().top;
		cauda.style.transition = "none";
		cauda.style.transform = "none";
		const layoutTop = cauda.getBoundingClientRect().top;
		const delta = layoutTop - visual;
		if (delta > 0.5) {
			cauda.style.transform = "translateY(" + -delta + "px)";
			void cauda.offsetHeight;
			cauda.style.transition = "transform 220ms ease";
			cauda.style.transform = "translateY(0)";
		} else {
			cauda.style.transition = "";
			cauda.style.transform = "";
		}
	}, [chat.messages, chat.streaming, stick]);

	useEffect(() => {
		return () => {
			if (quadroRef.current) window.cancelAnimationFrame(quadroRef.current);
		};
	}, []);

	const handleScroll = () => {
		const el = scrollRef.current;
		if (!el) return;
		const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
		// enquanto a rolagem programada está em curso a distância não diz nada
		// sobre a intenção do usuário
		if (rolagemAutomaticaRef.current) {
			if (nearBottom) rolagemAutomaticaRef.current = false;
			return;
		}
		setStick(nearBottom);
	};

	// qualquer gesto do usuário interrompe a rolagem automática
	const cancelarRolagemAutomatica = () => {
		if (quadroRef.current) {
			window.cancelAnimationFrame(quadroRef.current);
			quadroRef.current = null;
		}
		rolagemAutomaticaRef.current = false;
	};

	const scrollToBottom = () => {
		irParaOFim();
		setStick(true);
	};

	const resizeTextarea = () => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		if (!el.scrollHeight) return;
		el.style.height = Math.min(el.scrollHeight, 200) + "px";
	};

	useEffect(() => {
		resizeTextarea();
	}, [draft]);

	// as cores do anel saem do proprio arquivo da marca, entao trocar a logo
	// basta para a animacao acompanhar
	useEffect(() => {
		let ativo = true;
		fetch(logoResponde)
			.then(resposta => resposta.text())
			.then(svg => {
				const encontradas = extrairCoresDaMarca(svg);
				if (ativo && encontradas.length > 1) setCoresMarca(encontradas);
			})
			.catch(() => {});
		return () => {
			ativo = false;
		};
	}, []);

	useEffect(() => {
		const rodape = rodapeRef.current;
		const rolagem = scrollRef.current;
		if (!rodape || !rolagem || typeof window.ResizeObserver !== "function") return;
		const reservar = () => rolagem.style.setProperty("--repondeur-rodape", rodape.offsetHeight + "px");
		const observador = new window.ResizeObserver(reservar);
		observador.observe(rodape);
		reservar();
		return () => observador.disconnect();
	}, []);

	const { warmUp, carregarContexto } = chat;

	useEffect(() => {
		warmUp();
		if (carregarContexto) carregarContexto();
		if (textareaRef.current && !isCoarsePointer()) {
			textareaRef.current.focus();
		}
	}, [warmUp, carregarContexto]);

	const recordingRef = useRef(false);
	const dictationStartRef = useRef("");
	const mediaStreamRef = useRef(null);
	const audioCtxRef = useRef(null);
	const analyserRef = useRef(null);
	const rafRef = useRef(null);
	const canvasRef = useRef(null);

	const waveHistoryRef = useRef([]);
	const frameCountRef = useRef(0);

	const drawWave = () => {
		rafRef.current = requestAnimationFrame(drawWave);
		const canvas = canvasRef.current;
		const analyser = analyserRef.current;
		if (!canvas || !analyser) return;

		const data = new Uint8Array(analyser.fftSize);
		analyser.getByteTimeDomainData(data);
		let sum = 0;
		for (let i = 0; i < data.length; i++) {
			const desvio = (data[i] - 128) / 128;
			sum += desvio * desvio;
		}
		const rms = Math.sqrt(sum / data.length);

		const { width, height } = canvas;
		const slots = Math.floor(width / 7);
		const history = waveHistoryRef.current;
		if (frameCountRef.current++ % 3 === 0) {
			history.push(Math.min(1, rms * 4));
			if (history.length > slots) history.shift();
		}

		const ctx = canvas.getContext("2d");
		ctx.clearRect(0, 0, width, height);
		ctx.fillStyle = "#5f6b76";
		for (let i = 0; i < history.length; i++) {
			const barHeight = Math.max(2, history[i] * height);
			ctx.fillRect(i * 7, (height - barHeight) / 2, 2, barHeight);
		}
	};

	const startWaveform = () => {
		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return;
		navigator.mediaDevices
			.getUserMedia({ audio: true })
			.then(stream => {
				setMicError("");
				mediaStreamRef.current = stream;
				const track = stream.getAudioTracks()[0];
				setMicLabel((track && track.label) || "");
				const AudioCtx = window.AudioContext || window.webkitAudioContext;
				const audioCtx = new AudioCtx();
				audioCtxRef.current = audioCtx;
				if (audioCtx.state === "suspended") {
					audioCtx.resume();
				}
				const analyser = audioCtx.createAnalyser();
				analyser.fftSize = 512;
				audioCtx.createMediaStreamSource(stream).connect(analyser);
				analyserRef.current = analyser;
				waveHistoryRef.current = [];
				frameCountRef.current = 0;
				rafRef.current = requestAnimationFrame(drawWave);
			})
			.catch(() => {
				setMicError("Permita o acesso ao microfone no navegador para usar o ditado.");
				stopRecording();
			});
	};

	const stopWaveform = () => {
		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach(track => track.stop());
			mediaStreamRef.current = null;
		}
		if (audioCtxRef.current) {
			try {
				audioCtxRef.current.close();
			} catch (e) {}
			audioCtxRef.current = null;
		}
		analyserRef.current = null;
		waveHistoryRef.current = [];
		setMicLabel("");
	};

	const stopRecording = () => {
		recordingRef.current = false;
		if (recognitionRef.current) {
			recognitionRef.current.onend = null;
			recognitionRef.current.onresult = null;
			recognitionRef.current.onerror = null;
			try {
				recognitionRef.current.stop();
			} catch (e) {}
			recognitionRef.current = null;
		}
		stopWaveform();
		setRecording(false);
	};

	const cancelRecording = () => {
		stopRecording();
		setDraft(dictationStartRef.current);
	};

	const startRecognitionSession = () => {
		const recognition = new SpeechRecognitionImpl();
		recognition.lang = "pt-BR";
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.onresult = event => {
			let finais = "";
			let parcial = "";
			for (let i = 0; i < event.results.length; i++) {
				const result = event.results[i];
				if (result.isFinal) {
					finais += result[0].transcript;
				} else {
					parcial += result[0].transcript;
				}
			}
			setDraft(draftBaseRef.current + finais + parcial);
		};
		recognition.onerror = event => {
			const erro = (event && event.error) || "";
			if (erro === "no-speech" || erro === "aborted") {
				return;
			}

			const mensagens = {
				"not-allowed": "Permita o acesso ao microfone no navegador para usar o ditado.",
				"service-not-allowed": "Permita o acesso ao microfone no navegador para usar o ditado.",
				"audio-capture": "Nenhum microfone disponível para captura.",
				network: "Não foi possível conectar ao serviço de transcrição.",
			};

			setMicError(mensagens[erro] || "Não foi possível transcrever o áudio. Tente novamente.");
			stopRecording();
		};
		recognition.onend = () => {
			if (!recordingRef.current) {
				setRecording(false);
				return;
			}
			const atual = textareaRef.current ? textareaRef.current.value.replace(/\s+$/, "") : "";
			draftBaseRef.current = atual ? atual + " " : "";
			try {
				recognition.start();
			} catch (e) {
				stopRecording();
			}
		};
		recognitionRef.current = recognition;
		recognition.start();
	};

	const toggleRecording = () => {
		if (recordingRef.current) {
			stopRecording();
			return;
		}
		if (!SpeechRecognitionImpl) {
			setMicError("Seu navegador não suporta ditado por voz. Use o Google Chrome.");
			return;
		}
		if (!window.isSecureContext) {
			setMicError("O ditado por voz exige uma conexão segura (https).");
			return;
		}
		setMicError("");
		dictationStartRef.current = draft;
		draftBaseRef.current = draft.trim() ? draft.replace(/\s+$/, "") + " " : "";
		recordingRef.current = true;
		setRecording(true);
		startRecognitionSession();
		startWaveform();
	};

	useEffect(() => stopRecording, []);

	const submit = event => {
		if (event) event.preventDefault();
		const texto = draft.trim();
		if (!texto || chat.streaming) return;
		stopRecording();
		chat.sendMessage(texto);
		setDraft("");
		setStick(true);
		const el = textareaRef.current;
		if (el) el.style.height = "auto";
	};

	const handleKeyDown = event => {
		if (recording) return;
		if (event.key === "Enter" && !event.shiftKey && !isCoarsePointer()) {
			event.preventDefault();
			submit();
		}
	};

	const waitingFirstChunk =
		chat.streaming &&
		chat.messages.length > 0 &&
		chat.messages[chat.messages.length - 1].from === "bot" &&
		!chat.messages[chat.messages.length - 1].text;

	const nome = getNomeUsuario();

	return (
		<div className="repondeur-chat">
			{chat.messages.length === 0 && (
				<div className="repondeur-hero">
					<div className="repondeur-hero-logo-slot" aria-hidden="true" />
					<div className="repondeur-hero-title">
						<h2>
							{saudacaoPorHora(new Date().getHours())}
							{nome ? ", " + nome : ""}!
						</h2>
					</div>
					<p className="repondeur-hero-sub">Em que posso ajudar?</p>
				</div>
			)}

			<div
				className="repondeur-scroll"
				ref={scrollRef}
				onScroll={handleScroll}
				onWheel={cancelarRolagemAutomatica}
				onTouchStart={cancelarRolagemAutomatica}
			>
				<div className="repondeur-thread">
					{chat.messages.map(msg =>
						msg.from === "bot" && !msg.text ? null : (
							<ChatMessage key={msg.id} msg={msg} onFeedback={chat.sendFeedback} />
						)
					)}
					{waitingFirstChunk && (
						<div className="repondeur-cauda" ref={caudaRef}>
							{waitingFirstChunk && (
								<div className="repondeur-message repondeur-message-bot">
									<div className="repondeur-avatar" aria-hidden="true">
										<img className="repondeur-avatar-robo" src={roboResponde} alt="" />
									</div>
									<div className="repondeur-typing">
										<span />
										<span />
										<span />
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="repondeur-composer-wrap" ref={rodapeRef}>
				{!stick && (
					<button
						type="button"
						className="repondeur-scroll-down"
						onClick={scrollToBottom}
						aria-label="Ir para a mensagem mais recente"
					>
						<ArrowDownwardIcon fontSize="inherit" />
					</button>
				)}
				{chat.messages.length > 0 && (
					<div className="repondeur-conversa">
						<span className="repondeur-conversa-info">
							{chat.messages.length} {chat.messages.length === 1 ? "mensagem" : "mensagens"}
						</span>
						<ReactToPrint
							documentTitle="Essilor Conecta - Conversa Conecta Responde"
							onBeforeGetContent={() => {
								// liga a espera e libera já: o documento é montado em segundo
								// plano enquanto o aviso roda
								setGerandoPdf(true);
								inicioPdfRef.current = Date.now();
								return new Promise(resolve => setTimeout(resolve, 30));
							}}
							onBeforePrint={() => {
								// documento pronto: completa o piso de exibição do aviso, apaga
								// e dá um quadro para pintar antes de o diálogo travar a thread
								const restante = Math.max(0, 600 - (Date.now() - inicioPdfRef.current));
								return new Promise(resolve =>
									setTimeout(() => {
										setGerandoPdf(false);
										setTimeout(resolve, 60);
									}, restante)
								);
							}}
							onPrintError={() => setGerandoPdf(false)}
							trigger={() => (
								<Button
									id="btn-baixar-conversa"
									className="pdf-button repondeur-download"
									variant="link"
									title="Baixar conversa (PDF)"
									disabled={gerandoPdf}
									aria-busy={gerandoPdf}
								>
									{gerandoPdf ? (
										<>
											<span className="repondeur-spinner" aria-hidden="true" />
											Gerando PDF...
										</>
									) : (
										<>
											<FileEarmarkText size={16} className="me-1" />
											Baixar conversa (PDF)
										</>
									)}
								</Button>
							)}
							content={() => printRef.current}
						/>
						<div style={{ display: "none" }}>
							<RepondeurPrint ref={printRef} messages={chat.messages} contexto={chat.contexto} />
						</div>
					</div>
				)}
				<form className="repondeur-composer" onSubmit={submit}>
					{chat.messages.length === 0 && (
						<svg className="repondeur-beam" aria-hidden="true" focusable="false">
							<defs>
								<linearGradient
									id="repondeurBeamGrad"
									x1="0"
									y1="0"
									x2="0.5"
									y2="0"
									spreadMethod="repeat"
								>
									{coresMarca.concat(coresMarca[0]).map((cor, indice, lista) => (
										<stop
											key={indice}
											offset={(indice / (lista.length - 1)) * 100 + "%"}
											stopColor={cor}
										/>
									))}
									<animateTransform
										attributeName="gradientTransform"
										type="translate"
										from="0 0"
										to="0.5 0"
										dur="1.6s"
										repeatCount="indefinite"
									/>
								</linearGradient>
							</defs>
							<rect className="repondeur-beam-rect" pathLength="1000" />
						</svg>
					)}
					<textarea
						ref={textareaRef}
						rows={1}
						value={draft}
						maxLength={1000}
						className={recording ? "dictating" : ""}
						readOnly={recording}
						placeholder={recording ? "Ouvindo..." : "Escreva uma mensagem..."}
						onChange={event => setDraft(event.target.value)}
						onKeyDown={handleKeyDown}
					/>
					{recording ? (
						<div className="repondeur-composer-bar">
							<canvas ref={canvasRef} className="repondeur-wave" width="560" height="28" />
							{micLabel && (
								<span className="repondeur-mic-label" title={micLabel}>
									{micLabel}
								</span>
							)}
							<button
								type="button"
								className="repondeur-cancel"
								onClick={cancelRecording}
								aria-label="Cancelar gravação"
							>
								<CloseIcon fontSize="inherit" />
							</button>
							<button
								type="button"
								className="repondeur-send"
								onClick={stopRecording}
								aria-label="Confirmar texto ditado"
							>
								<CheckIcon fontSize="inherit" />
							</button>
						</div>
					) : (
						<div className="repondeur-composer-bar">
							<button
								type="button"
								className="repondeur-mic"
								onClick={toggleRecording}
								disabled={!SpeechRecognitionImpl}
								title={
									SpeechRecognitionImpl ? "Falar mensagem" : "Ditado por voz disponível no Chrome e no Edge"
								}
								aria-label={
									SpeechRecognitionImpl ? "Falar mensagem" : "Ditado por voz disponível no Chrome e no Edge"
								}
							>
								<MicIcon fontSize="inherit" />
							</button>
							<button
								type="submit"
								className="repondeur-send"
								disabled={chat.streaming || !draft.trim()}
								aria-label="Enviar mensagem"
							>
								<ArrowUpwardIcon fontSize="inherit" />
							</button>
						</div>
					)}
				</form>
				{micError && <p className="repondeur-mic-error">{micError}</p>}
			</div>
		</div>
	);
};

export default ChatWindow;
